import { BaseModel } from '@models/generic.model'

/**
 * Ciclo recursal da sessão de disputa (specs 023/026) — Pregão/Concorrência, 1 ciclo por
 * participante por rodada da sessão. Desde a spec 026 o ciclo tem DUAS etapas no mesmo registro:
 * a intenção de recurso (bloco original) e o recurso em si (bloco `appeal*`), com o estado
 * consolidado vindo DERIVADO do servidor em `stage` — a tela nunca o calcula.
 */
export enum NoticeSessionAppealStatus {
	PENDING = 1,
	GRANTED = 2,
	REJECTED = 3
}

/** Estado derivado do ciclo (spec 026) — espelho do enum do servidor, nunca calculado na tela. */
export enum NoticeSessionAppealStage {
	/** Intenção enviada, aguardando resposta do agente. */
	INTENT_PENDING = 'INTENT_PENDING',
	/** Intenção indeferida — terminal, sem etapa de recurso. */
	INTENT_REJECTED = 'INTENT_REJECTED',
	/** Intenção deferida, prazo vigente, recurso ainda não apresentado. */
	AWAITING_APPEAL = 'AWAITING_APPEAL',
	/** Prazo do recurso vencido sem apresentação — preclusão, ciclo encerrado. */
	PRECLUDED = 'PRECLUDED',
	/** Recurso apresentado, aguardando decisão do agente. */
	APPEAL_PENDING = 'APPEAL_PENDING',
	/** Recurso decidido (provido/improvido) — ou ciclo legado da spec 023, já decidido. */
	APPEAL_DECIDED = 'APPEAL_DECIDED'
}

export class NoticeSessionAppealSupplier {
	id: string
	document: string
	name: string

	constructor(source?: Partial<NoticeSessionAppealSupplier>) {
		if (source) {
			this.id = source.id
			this.document = source.document
			this.name = source.name
		}
	}
}

export class NoticeSessionAppeal extends BaseModel {
	supplierEnrollmentNoticeId: string
	supplier: NoticeSessionAppealSupplier
	attachmentId: string
	description: string
	status: NoticeSessionAppealStatus
	responseAttachmentId: string
	responseJustification: string
	decidedAt: string

	// spec 026 — etapa do Recurso
	appealDeadlineAt: string
	appealDescription: string
	appealAttachmentId: string
	appealSubmittedAt: string
	appealStatus: NoticeSessionAppealStatus
	appealResponseJustification: string
	appealResponseAttachmentId: string
	appealDecidedAt: string
	stage: NoticeSessionAppealStage

	constructor(source?: Partial<NoticeSessionAppeal>) {
		super(source)

		if (source) {
			this.supplierEnrollmentNoticeId = source.supplierEnrollmentNoticeId
			this.supplier = source.supplier ? new NoticeSessionAppealSupplier(source.supplier) : null
			this.attachmentId = source.attachmentId
			this.description = source.description
			this.status = source.status
			this.responseAttachmentId = source.responseAttachmentId
			this.responseJustification = source.responseJustification
			this.decidedAt = source.decidedAt

			this.appealDeadlineAt = source.appealDeadlineAt
			this.appealDescription = source.appealDescription
			this.appealAttachmentId = source.appealAttachmentId
			this.appealSubmittedAt = source.appealSubmittedAt
			this.appealStatus = source.appealStatus
			this.appealResponseJustification = source.appealResponseJustification
			this.appealResponseAttachmentId = source.appealResponseAttachmentId
			this.appealDecidedAt = source.appealDecidedAt
			this.stage = source.stage
		}
	}

	get stageIntentPending(): boolean {
		return this.stage === NoticeSessionAppealStage.INTENT_PENDING
	}

	get stageIntentRejected(): boolean {
		return this.stage === NoticeSessionAppealStage.INTENT_REJECTED
	}

	get stageAwaitingAppeal(): boolean {
		return this.stage === NoticeSessionAppealStage.AWAITING_APPEAL
	}

	get stagePrecluded(): boolean {
		return this.stage === NoticeSessionAppealStage.PRECLUDED
	}

	get stageAppealPending(): boolean {
		return this.stage === NoticeSessionAppealStage.APPEAL_PENDING
	}

	get stageAppealDecided(): boolean {
		return this.stage === NoticeSessionAppealStage.APPEAL_DECIDED
	}

	/** Desfecho do RECURSO (etapa 2) — distinto de `statusGranted`, que é a resposta à intenção. */
	get appealDecisionGranted(): boolean {
		return this.appealStatus === NoticeSessionAppealStatus.GRANTED
	}

	get appealDecisionRejected(): boolean {
		return this.appealStatus === NoticeSessionAppealStatus.REJECTED
	}

	/**
	 * Rótulo do estágio derivado. Ciclos legados (spec 023, deferidos sem prazo) chegam como
	 * `APPEAL_DECIDED` com `appealStatus` nulo — o rótulo cai para a decisão antiga (`statusLabel`)
	 * para não reescrever a leitura de sessões já encerradas. Sem `stage` (resposta de servidor
	 * anterior), idem.
	 */
	get stageLabel(): string {
		switch (this.stage) {
			case NoticeSessionAppealStage.INTENT_PENDING:
				return 'Intenção pendente'
			case NoticeSessionAppealStage.INTENT_REJECTED:
				return 'Intenção indeferida'
			case NoticeSessionAppealStage.AWAITING_APPEAL:
				return 'Aguardando recurso'
			case NoticeSessionAppealStage.PRECLUDED:
				return 'Precluso'
			case NoticeSessionAppealStage.APPEAL_PENDING:
				return 'Recurso em análise'
			case NoticeSessionAppealStage.APPEAL_DECIDED:
				switch (this.appealStatus) {
					case NoticeSessionAppealStatus.GRANTED:
						return 'Provido'
					case NoticeSessionAppealStatus.REJECTED:
						return 'Improvido'
					default:
						return this.statusLabel
				}
			default:
				return this.statusLabel
		}
	}

	get statusPending(): boolean {
		return this.status === NoticeSessionAppealStatus.PENDING
	}

	get statusGranted(): boolean {
		return this.status === NoticeSessionAppealStatus.GRANTED
	}

	get statusRejected(): boolean {
		return this.status === NoticeSessionAppealStatus.REJECTED
	}

	get statusLabel(): string {
		switch (this.status) {
			case NoticeSessionAppealStatus.GRANTED:
				return 'Aceito'
			case NoticeSessionAppealStatus.REJECTED:
				return 'Rejeitado'
			default:
				return 'Pendente'
		}
	}
}
